import { type Attachment, AttachmentType } from '@/types/models';

/**
 * Printing photos with a work order (#498).
 *
 * The detail page's print modal and the `/print/work-order/[id]` page live in
 * different tabs, so the only thing they share is the URL. `buildWorkOrderPrintUrl`
 * writes the `photos` param and `parsePhotoTypesParam` reads it back; everything
 * else here is the selection and grouping both sides agree on.
 */

/** Print order for the appendix — the order the job happened, then paperwork. */
const PRINT_TYPE_ORDER: readonly AttachmentType[] = [
	AttachmentType.BeforePhoto,
	AttachmentType.AfterPhoto,
	AttachmentType.Photo,
	AttachmentType.Receipt,
	AttachmentType.Invoice,
	AttachmentType.Misc,
	AttachmentType.Literature,
	AttachmentType.Quote,
	AttachmentType.Plan,
	AttachmentType.Insurance,
];

const GROUP_HEADING: Record<AttachmentType, string> = {
	[AttachmentType.BeforePhoto]: 'Before Photos',
	[AttachmentType.AfterPhoto]: 'After Photos',
	[AttachmentType.Photo]: 'Photos',
	[AttachmentType.Receipt]: 'Receipts',
	[AttachmentType.Invoice]: 'Invoices',
	[AttachmentType.Misc]: 'Other Images',
	[AttachmentType.Literature]: 'Product Literature',
	[AttachmentType.Quote]: 'Quotes & Contracts',
	[AttachmentType.Plan]: 'Plans & Drawings',
	[AttachmentType.Insurance]: 'Insurance',
};

const KNOWN_TYPES = new Set<string>(Object.values(AttachmentType));

const isAttachmentType = (value: string): value is AttachmentType => KNOWN_TYPES.has(value);

/** `attachmentType` is canonical; `type` is the legacy alias older rows still carry. */
const typeOf = (attachment: Attachment): AttachmentType | null => {
	const raw = attachment.attachmentType || attachment.type || '';
	return isAttachmentType(raw) ? raw : null;
};

const printRank = (type: AttachmentType): number => {
	const index = PRINT_TYPE_ORDER.indexOf(type);
	return index === -1 ? PRINT_TYPE_ORDER.length : index;
};

const createdAtMs = (attachment: Attachment): number => {
	const ms = new Date(attachment.createdAt).getTime();
	return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Read the `photos` query param. Accepts the string form and the array form
 * expo-router produces for a repeated param; unknown values are dropped.
 */
export const parsePhotoTypesParam = (raw: string | string[] | undefined | null): AttachmentType[] => {
	const parts = (Array.isArray(raw) ? raw : [raw ?? '']).flatMap((part) => part.split(','));
	const seen = new Set<AttachmentType>();
	for (const part of parts) {
		const value = part.trim();
		if (isAttachmentType(value)) seen.add(value);
	}
	return [...seen];
};

/**
 * The print route for a work order. With no types the URL is exactly what the
 * print button opened before #498, so the plain form keeps printing unchanged.
 */
export const buildWorkOrderPrintUrl = (workOrderId: string, types: readonly AttachmentType[]): string => {
	const base = `/print/work-order/${workOrderId}`;
	if (types.length === 0) return base;
	return `${base}?${new URLSearchParams({ photos: types.join(',') }).toString()}`;
};

/**
 * The images to print, in print order. Only IMAGES qualify — a PDF filed as a
 * "before photo" cannot be laid out as a picture — and within a type the oldest
 * comes first, which for before/after photos is the order the work happened.
 */
export const selectPrintablePhotos = (
	attachments: readonly Attachment[],
	types: readonly AttachmentType[],
): Attachment[] => {
	if (types.length === 0) return [];
	const wanted = new Set(types);
	return attachments
		.filter((a) => {
			const type = typeOf(a);
			return Boolean(a.isImage) && type !== null && wanted.has(type);
		})
		.sort((a, b) => {
			const byType = printRank(typeOf(a) as AttachmentType) - printRank(typeOf(b) as AttachmentType);
			return byType !== 0 ? byType : createdAtMs(a) - createdAtMs(b);
		});
};

export interface PrintPhotoGroup {
	type: AttachmentType;
	heading: string;
	photos: Attachment[];
}

/** Group already-selected photos under the heading each was filed as. */
export const groupPhotosForPrint = (photos: readonly Attachment[]): PrintPhotoGroup[] => {
	const groups = new Map<AttachmentType, Attachment[]>();
	for (const photo of photos) {
		const type = typeOf(photo);
		if (!type) continue;
		const list = groups.get(type) ?? [];
		list.push(photo);
		groups.set(type, list);
	}
	return [...groups.entries()]
		.sort(([a], [b]) => printRank(a) - printRank(b))
		.map(([type, list]) => ({ type, heading: GROUP_HEADING[type], photos: list }));
};

export interface PhotoTypeOption {
	type: AttachmentType;
	label: string;
	count: number;
}

/**
 * What the print modal offers: one row per image type actually present on the
 * work order, with a count, so a PM never ticks a box that prints nothing.
 */
export const photoTypeOptions = (attachments: readonly Attachment[]): PhotoTypeOption[] => {
	const counts = new Map<AttachmentType, number>();
	for (const attachment of attachments) {
		const type = typeOf(attachment);
		if (!attachment.isImage || !type) continue;
		counts.set(type, (counts.get(type) ?? 0) + 1);
	}
	return [...counts.entries()]
		.sort(([a], [b]) => printRank(a) - printRank(b))
		.map(([type, count]) => ({ type, label: GROUP_HEADING[type], count }));
};
